/**
 * P-36 (D-193) — the participant rule of a new discussion. The SAME rule as the
 * server's (`ote-backend/src/services/discussion.service.ts`): the number of
 * DISTINCT people, the opener included, and at least 3.
 */
export const MIN_DISCUSSION_PARTICIPANTS = 3;
/** P-45b (D-210, QX-210..): a conversation needs 2 (the opener + one). */
export const MIN_CONVERSATION_PARTICIPANTS = 2;
export type DiscussionKind = 'conversation' | 'discussion';
export const minParticipantsFor = (kind: DiscussionKind): number =>
  kind === 'conversation' ? MIN_CONVERSATION_PARTICIPANTS : MIN_DISCUSSION_PARTICIPANTS;

/** Distinct participants: the opener plus the ones picked (empty ids ignored). */
export function countDiscussionParticipants(openerId: string | null | undefined, pickedIds: readonly string[]): number {
  const s = new Set<string>();
  if (openerId) s.add(openerId);
  for (const id of pickedIds) if (id) s.add(id);
  return s.size;
}

export function discussionHasEnoughParticipants(openerId: string | null | undefined, pickedIds: readonly string[], kind: DiscussionKind = 'discussion'): boolean {
  return countDiscussionParticipants(openerId, pickedIds) >= minParticipantsFor(kind);
}

export interface DiscussionPerson { id: string; role: string; organization_id?: string | null }

/** Splits the one participants list into what the server takes: OTE side (admin / agent) and the customer's people. */
export function splitDiscussionParticipants(pickedIds: readonly string[], people: readonly DiscussionPerson[], openerId: string | null | undefined): {
  additional_agent_ids: string[];
  extra_customer_ids: string[];
} {
  const byId = new Map(people.map((p) => [p.id, p]));
  const additional_agent_ids: string[] = [];
  const extra_customer_ids: string[] = [];
  for (const id of new Set(pickedIds)) {
    if (id === openerId) continue;
    const p = byId.get(id);
    if (!p) continue;
    if (p.role === 'customer_viewer' || p.role === 'customer_manager') extra_customer_ids.push(id);
    else additional_agent_ids.push(id);
  }
  return { additional_agent_ids, extra_customer_ids };
}

/** After a save: the discussion's own chat screen. */
export const discussionChatPath = (ticketId: string): string => `/discussions?ticket=${ticketId}`;

/**
 * A customer-side login never opens a discussion (the option is hidden; the
 * server answers 403). The internal-only employee opens an INTERNAL one only
 * (D-196, QX-141): no customer, OTE employees as participants.
 */
export function canOpenDiscussion(user: { role?: string } | null | undefined, _internalOnly: boolean): boolean {
  return user?.role === 'admin' || user?.role === 'agent';
}

/** "דיון חדש" from a customer's page: not for the internal-only employee (she has no customer). */
export function canOpenCustomerDiscussion(user: { role?: string } | null | undefined, internalOnly: boolean): boolean {
  return canOpenDiscussion(user, internalOnly) && !internalOnly;
}

/**
 * Who may CLOSE a discussion (D-193 Q-168, T-60): the one who opened it and the
 * OTE manager. Anything that is not a discussion: no extra rule. The server
 * decides; this only hides the button.
 */
export function canCloseDiscussionUi(
  ticket: { task_type?: string | null; opened_by_id?: string | null },
  user: { id?: string; role?: string } | null | undefined,
): boolean {
  if (ticket.task_type !== 'discussion') return true;
  if (user?.role === 'admin') return true;
  return !!user?.id && !!ticket.opened_by_id && ticket.opened_by_id === user.id;
}

/**
 * FIX-B23 (QX-340): the URL of the conversations page after picking a conversation (`ticket`) or
 * going back to the list (`null`). The chosen chip (`?kind=`) stays; the rest is dropped as before.
 */
export function conversationPageParams(prev: URLSearchParams, ticketId: string | null): URLSearchParams {
  const next = new URLSearchParams();
  const kind = prev.get('kind');
  if (kind) next.set('kind', kind);
  const tab = prev.get('tab'); // D-326: the chosen tab stays
  if (tab) next.set('tab', tab);
  if (ticketId) next.set('ticket', ticketId);
  return next;
}
